import React, { useMemo } from "react";
import { Modal, Row, Col, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { createElementIdFactory, createSLMOwnerModalElementIds } from "utils/elementIds";
import "./SLMOwnerModal.css";
import "containers/slm/lease/SLMLeaseDetail.css";

export default function SLMOwnerModal({
  show,
  onHide,
  groupLabel,
  searchString,
  onSearchStringChange,
  onSearchKeyDown,
  onSearch,
  isSearching = false,
  interstateFlag,
  onInterstateFlagChange,
  createOwnerHref,
  editOwnerHref,
  createButtonLabel,
  showAddAnotherOwnerLink,
  onAddAnotherOwnerLink,
  selectedOwner,
  onOwnerChange,
  ownerOptions = [],
  ownerMenuIsOpen,
  ownerPhone,
  ownerLocale,
  selectedContact,
  onContactChange,
  onCreateContactOption,
  contactOptions = [],
  isContactOptionsLoading = false,
  contactPhone,
  contactEmail,
  selectedType,
  onTypeChange,
  typeOptions = [],
  showAddAnotherContact,
  onAddAnotherContact,
}) {
  const createId = useMemo(() => createElementIdFactory("slm-owner-modal"), []);
  const elementIds = useMemo(() => createSLMOwnerModalElementIds(createId), [createId]);
  const selectMenuPortalTarget = typeof window !== "undefined" ? document.body : null;
  const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2055 }),
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      id={elementIds.container}
      dialogClassName="slm-owner-modal-wide"
      contentClassName="slm-owner-modal-content"
      fullscreen="lg-down"
    >
      <Modal.Header closeButton>
        <Modal.Title>{groupLabel}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="slm-owner-modal-body">
        <Form id={elementIds.form} className="slm-owner-modal-form">
          <Row className="g-3 align-items-start slm-owner-modal-top-row">
            <Form.Group as={Col} xs={12} xl={7} controlId="ownerSearchString">
              <Form.Label className="text-right" id={elementIds.searchLabel}>Search {groupLabel}</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  id={elementIds.searchInput}
                  onChange={onSearchStringChange}
                  value={searchString}
                  onKeyDown={onSearchKeyDown}
                  as="input"
                  type="text"
                  placeholder="Search entities"
                />
                <Button id={elementIds.searchButton} onClick={onSearch} variant="outline-secondary" size="sm" type="button" disabled={isSearching}>
                  {isSearching ? <><Spinner animation="border" size="sm" role="status" aria-hidden="true" /> Searching...</> : "Search"}
                </Button>
              </InputGroup>
              <div className="slm-owner-modal-interstate-check">
                <input
                  className="slm-owner-modal-interstate-input"
                  id={elementIds.interstateCheckbox}
                  checked={interstateFlag}
                  onChange={onInterstateFlagChange}
                  type="checkbox"
                  name="ownerInterstateFlag"
                />
                <label className="slm-owner-modal-interstate-label" id={elementIds.interstateLabel} htmlFor={elementIds.interstateCheckbox}>
                  Search interstate
                </label>
              </div>
            </Form.Group>
            <Col xs={12} xl={5} className="slm-owner-modal-actions-col">
              <div className="slm-owner-modal-actions">
                <a target="_blank" rel="noopener noreferrer" href={createOwnerHref}>
                  <Button id={elementIds.createOwnerButton} variant="primary" type="button" className="slm-compact-action-btn" size="m">
                    {createButtonLabel}
                  </Button>
                </a>
                {editOwnerHref && (
                  <a target="_blank" rel="noopener noreferrer" href={editOwnerHref}>
                    <Button id={elementIds.editOwnerButton} variant="primary" type="button" className="slm-compact-action-btn" size="m">
                      Edit Owner
                    </Button>
                  </a>
                )}
                {showAddAnotherOwnerLink && (
                  <Button id={elementIds.addOwnerLinkButton} type="button" onClick={onAddAnotherOwnerLink} className="slm-compact-action-btn" variant="primary" size="m">
                    Add Another {groupLabel}
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          <Row className="g-3 align-items-start mt-1">
            <Form.Group as={Col} xs={12} xl={4} controlId="owner_org_id">
              <Form.Label className="text-right" id={elementIds.ownerLabel}>{groupLabel}</Form.Label>
              <Select
                isClearable
                value={selectedOwner}
                onChange={onOwnerChange}
                options={ownerOptions}
                menuIsOpen={ownerMenuIsOpen}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={selectMenuStyles}
                inputId={elementIds.ownerInput}
                instanceId={elementIds.ownerInstance}
              />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="owner_phone">
              <Form.Label className="text-right" id={elementIds.ownerPhoneLabel}>{groupLabel} Phone</Form.Label>
              <Form.Control id={elementIds.ownerPhoneInput} value={ownerPhone} as="input" type="text" disabled readOnly />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="owner_locale">
              <Form.Label className="text-right" id={elementIds.ownerLocaleLabel}>Suburb State Postcode</Form.Label>
              <Form.Control id={elementIds.ownerLocaleInput} value={ownerLocale} as="input" type="text" disabled readOnly />
            </Form.Group>
          </Row>

          <Row className="g-3 align-items-start mt-1">
            <Form.Group as={Col} xs={12} xl={4} controlId="owner_person_id">
              <Form.Label className="text-right" id={elementIds.contactLabel}>Contact</Form.Label>
              <CreatableSelect
                isClearable
                value={selectedContact}
                onChange={onContactChange}
                options={contactOptions}
                onCreateOption={onCreateContactOption}
                isDisabled={isContactOptionsLoading}
                isLoading={isContactOptionsLoading}
                placeholder={isContactOptionsLoading ? "Loading contacts..." : "Select..."}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={selectMenuStyles}
                inputId={elementIds.contactInput}
                instanceId={elementIds.contactInstance}
              />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="contact_phone">
              <Form.Label className="text-right" id={elementIds.contactPhoneLabel}>Contact Phone</Form.Label>
              <Form.Control id={elementIds.contactPhoneInput} value={contactPhone} as="input" type="text" disabled readOnly />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} xl={4} controlId="contact_email">
              <Form.Label className="text-right" id={elementIds.contactEmailLabel}>Contact Email</Form.Label>
              <Form.Control id={elementIds.contactEmailInput} value={contactEmail} as="input" type="email" disabled readOnly />
            </Form.Group>
          </Row>

          <Row className="g-3 align-items-start mt-1">
            <Form.Group as={Col} xs={12} xl={4} controlId="owner_link_type">
              <Form.Label className="text-right" id={elementIds.typeLabel}>{groupLabel}</Form.Label>
              <Select
                isClearable
                value={selectedType}
                onChange={onTypeChange}
                options={typeOptions}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                styles={selectMenuStyles}
                inputId={elementIds.typeInput}
                instanceId={elementIds.typeInstance}
              />
            </Form.Group>
            <Col xs={12} xl={8} />
          </Row>

          {isContactOptionsLoading && (
            <Row className="mt-2">
              <Col xs={12} className="d-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" role="status" />
                <span>Loading owner contacts...</span>
              </Col>
            </Row>
          )}

          <Row className="mt-3">
            <Col xs={12}>
              {showAddAnotherContact && (
                <Button id={elementIds.addContactButton} type="button" onClick={onAddAnotherContact} className="slm-compact-action-btn" variant="primary" size="m">
                  Add Another Contact
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
